import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { LoadingComponent } from '@/components/loading/loading.component';
import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

@Component({
  selector: 'document-special-measures-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    LoadingComponent,
  ],
  templateUrl: './document-special-measures-form.component.html',
  styleUrl: './document-special-measures-form.component.scss',
})
export class DocumentSpecialMeasuresFormComponent {
  @Input() id: number | string = 0;
  @Input() specialMeasures: any = ''
  // Plantilla de medidas propia del producto (p.special_measures), reutilizada como valores
  // por defecto al añadir una línea nueva con el botón "+" — ver addItem().
  @Input() defaultMeasures: any = null
  @Output() closeFunction = new EventEmitter<any>();
  @Output() acceptFunction = new EventEmitter<any>();

  public loading = signal(false);

  public saveForm: FormGroup = new FormGroup({});

  constructor(
    private formBuilder: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.createForm();
  }

  acceptFormFunction() {
    this.saveEntity()
  }

  closeFormFunction() {
    this.closeFunction.emit();
  }

  createForm() {
    this.saveForm = new FormGroup({
      measuresArray: this.formBuilder.array([]),
    });

    if (Array.isArray(this.specialMeasures) && this.specialMeasures.length > 0) {
      this.specialMeasures.forEach((m: any) => this.addItem(m));
    } else {
      this.addItem(this.specialMeasures);
    }
  }

  get measuresArray(): FormArray {
    return this.saveForm.get('measuresArray') as FormArray;
  }

  getControl(index: number, controlName: string): FormControl {
    return this.measuresArray.at(index).get(controlName) as FormControl;
  }

  addItem(data?: any) {
    // Sin datos propios (botón "+"), parte de la plantilla de medidas del producto en vez
    // de 1/1/1 genéricos, igual que la primera línea.
    const base = data ?? this.defaultMeasures;
    const group = this.formBuilder.group({
      width: [base?.w ?? 1, [Validators.min(0)]],
      height: [base?.h ?? 1, [Validators.min(0)]],
      thickness: [base?.t ?? 1, [Validators.min(0)]],
      quantity: [data?.q ?? 1, [Validators.min(0)]],
    });
    this.measuresArray.push(group);
  }

  // Un valor nulo o 0 no participa en el cálculo del volumen (se trata como
  // neutro, no como 0), para permitir omitir dimensiones que no apliquen
  // (p.ej. un producto sin grosor) sin anular el resto.
  private effectiveValue(value: any): number {
    if (value === null || value === undefined || value === '') return 1;
    const num = Number(value);
    return isNaN(num) || num === 0 ? 1 : num;
  }

  removeItem(index: number) {
    if (this.measuresArray.length > 1) {
      this.measuresArray.removeAt(index);
    }
  }

  saveEntity() {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    const { measuresArray } = this.saveForm.getRawValue();

    const totalVolume = measuresArray.reduce((acc: number, item: any) => {
      const volume = this.effectiveValue(item.width)
        * this.effectiveValue(item.height)
        * this.effectiveValue(item.thickness)
        * this.effectiveValue(item.quantity);
      return acc + volume;
    }, 0);

    this.acceptFunction.emit({
      totalVolume,
      details: measuresArray.map((item: any) => {
        return {
          w: item.width,
          h: item.height,
          t: item.thickness,
          q: item.quantity
        }
      })
    });
  }

}